import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Database,
  ExternalLink,
  Flame,
  MessageCircle,
  Search,
  Store,
} from "lucide-react";
import { COUNTRIES, COUNTRY_CODES } from "@/data/stores";
import { useLanguage } from "@/context/LanguageContext";
import { buildWhatsAppUrl } from "@/data/translations";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import LeadForm from "@/components/LeadForm";
import ShippingCalculator from "@/components/ShippingCalculator";
import { trackEvent } from "@/lib/analytics";

export default function CountryPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t, lang, isRTL } = useLanguage();
  const trackedCountryRef = useRef("");

  const country = useMemo(() => {
    const raw = (code || "").trim().toLowerCase();
    if (!raw) return null;

    const slugToCode = {
      tr: "TR",
      turkey: "TR",
      cn: "CN",
      china: "CN",
      us: "US",
      usa: "US",
      america: "US",
      "united-states": "US",
      ae: "AE",
      uae: "AE",
      emirates: "AE",
      "united-arab-emirates": "AE",
      fr: "FR",
      france: "FR",
      gb: "GB",
      uk: "GB",
      britain: "GB",
      "united-kingdom": "GB",
    };

    const resolvedCode = slugToCode[raw] || raw.toUpperCase();
    return COUNTRIES[resolvedCode] || null;
  }, [code]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [code]);

  useEffect(() => {
    if (!country) return;
    if (trackedCountryRef.current === country.code) return;
    trackedCountryRef.current = country.code;
    const countryName = lang === "ar" ? country.name_ar : country.name_en;
    trackEvent("country_page_view", {
      routeName: "country_page",
      country: { code: country.code, name: countryName },
      metadata: {
        stores_count: Array.isArray(country.stores) ? country.stores.length : 0,
      },
    });
    if (country.code === "CN") {
      trackEvent("china_suppliers_section_view", {
        routeName: "country_page",
        country: { code: country.code, name: countryName },
        metadata: { source: "country_page" },
      });
    }
  }, [country, lang]);

  // Guard: invalid code → redirect home
  useEffect(() => {
    if (!country) navigate("/", { replace: true });
  }, [country, navigate]);

  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("ALL");

  const cp = t.countryPage;

  // Derive categories
  const categories = useMemo(() => {
    if (!country) return [];
    const set = new Set();
    country.stores.forEach((s) =>
      set.add(lang === "ar" ? s.category_ar : s.category_en)
    );
    return Array.from(set);
  }, [country, lang]);

  const filteredStores = useMemo(() => {
    if (!country) return [];
    const q = query.trim().toLowerCase();
    return country.stores.filter((s) => {
      const cat = lang === "ar" ? s.category_ar : s.category_en;
      const desc = lang === "ar" ? s.desc_ar : s.desc_en;
      const matchesCat = activeCategory === "ALL" || cat === activeCategory;
      const matchesQuery =
        !q ||
        s.name.toLowerCase().includes(q) ||
        cat.toLowerCase().includes(q) ||
        desc.toLowerCase().includes(q);
      return matchesCat && matchesQuery;
    });
  }, [country, query, activeCategory, lang]);

  if (!country) return null;

  const countryName = lang === "ar" ? country.name_ar : country.name_en;
  const countryTagline = lang === "ar" ? country.tagline_ar : country.tagline_en;
  const trackCountryWhatsapp = (source, extra = {}) => {
    trackEvent("whatsapp_click", {
      routeName: "country_page",
      country: { code: country.code, name: countryName },
      metadata: { source, ...extra },
    });
    trackEvent("contact_click", {
      routeName: "country_page",
      country: { code: country.code, name: countryName },
      metadata: { source, channel: "whatsapp", ...extra },
    });
  };

  return (
    <main
      className="min-h-screen overflow-x-hidden bg-[#FAFAFA] text-[#111] dark:bg-[#080808] dark:text-[#F6F7FB]"
      data-testid={`country-page-${country.code}`}
    >
      <Navbar />

      {/* HERO */}
      <section className="relative px-5 pt-28 pb-12 md:px-8 md:pt-36 md:pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav
            className="mb-8 flex items-center gap-2 text-sm text-[#555] dark:text-[#BAC4D4]"
            data-testid="country-breadcrumb"
          >
            <Link to="/" className="hover:text-[#F37021] transition-colors">
              {cp.breadcrumbHome}
            </Link>
            <ChevronRight className={`w-3.5 h-3.5 text-[#999] ${isRTL ? 'rotate-180' : ''}`} />
            <Link to="/#countries" className="hover:text-[#F37021] transition-colors">
              {cp.breadcrumbCountries}
            </Link>
            <ChevronRight className={`w-3.5 h-3.5 text-[#999] ${isRTL ? 'rotate-180' : ''}`} />
            <span className="font-medium text-[#111] dark:text-white">{countryName}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-end">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex items-center gap-3 mb-6"
              >
                <img
                  src={`https://flagcdn.com/w80/${country.flag}.png`}
                  alt={countryName}
                  className="w-10 h-7 object-cover rounded-[3px] shadow-sm"
                />
                <span className="inline-flex items-center gap-2 rounded-full border border-[#F37021]/18 bg-[#FFF3EA] px-3.5 py-1.5 text-xs font-semibold tracking-[0.3em] uppercase text-[#C95D13]">
                  — {cp.intro} · {country.stores.length} {cp.storesCount}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="font-display text-[40px] font-bold leading-[1.05] text-[#111] sm:text-6xl md:text-7xl dark:text-white"
                data-testid="country-title"
              >
                {cp.shopFrom}{" "}
                <span className="text-[#F37021]">{countryName}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-4 max-w-xl text-base leading-relaxed text-[#555] sm:text-lg md:mt-5 md:text-xl dark:text-[#BAC4D4]"
              >
                {countryTagline}
              </motion.p>

              {/* Stats */}
              <div className="mt-8 flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-full border border-black/6 bg-white px-4 py-2.5 shadow-[0_10px_24px_rgba(16,24,40,0.05)] dark:border-white/10 dark:bg-[#121212]">
                  <Store className="w-4 h-4 text-[#F37021]" />
                  <span className="text-sm font-medium">
                    {country.stores.length} {cp.storesCount}
                  </span>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-black/6 bg-white px-4 py-2.5 shadow-[0_10px_24px_rgba(16,24,40,0.05)] dark:border-white/10 dark:bg-[#121212]">
                  <span className="text-xs font-semibold text-[#F37021]">{cp.currency}</span>
                  <span className="text-sm font-medium">{country.currency}</span>
                </div>
                <a
                  href={buildWhatsAppUrl(t.common.whatsappCountryMsg(countryName))}
                  onClick={() => trackCountryWhatsapp("country_hero")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] text-white text-sm font-medium flex items-center gap-2 transition-all hover:-translate-y-0.5 shadow-[0_12px_26px_rgba(37,211,102,0.24)]"
                  data-testid="country-hero-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  {cp.orderVia}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="relative aspect-[16/10] sm:aspect-[5/4] rounded-[28px] md:rounded-[32px] overflow-hidden border border-black/6 shadow-[0_24px_60px_rgba(16,24,40,0.12)]"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${country.heroImage})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute top-5 right-5 rtl:left-5 rtl:right-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20">
                  <img
                    src={`https://flagcdn.com/w40/${country.flag}.png`}
                    alt={countryName}
                    className="w-5 h-[14px] object-cover rounded-[2px]"
                  />
                  <span className="text-[11px] font-semibold tracking-wider uppercase text-white">
                    {country.code}
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 rtl:right-5 rtl:left-auto text-white">
                  <div className="font-display text-3xl font-bold leading-none">
                    {countryName}
                  </div>
                  <div className="text-xs text-white/80 mt-1 uppercase tracking-widest">
                    {country.currency}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="sticky top-16 z-30 bg-[#FAFAFA]/90 px-5 py-4 backdrop-blur-xl md:top-20 md:px-8 dark:bg-[#080808]/92">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 rounded-[28px] border border-black/6 bg-white/95 p-4 shadow-[0_16px_40px_rgba(16,24,40,0.08)] md:flex-row md:items-center md:justify-between dark:border-white/10 dark:bg-[#121212]/96">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 text-[#777] dark:text-[#8FA1B8] ${isRTL ? 'right-4' : 'left-4'}`} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={cp.searchPlaceholder}
              className={`h-11 w-full rounded-full border border-black/10 bg-[#FAFAFA] text-sm transition-all focus:border-[#F37021] focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:border-white/10 dark:bg-[#121212] dark:text-white ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'}`}
              data-testid="country-search-input"
            />
          </div>

          {/* Category pills */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="country-categories">
            <button
              onClick={() => setActiveCategory("ALL")}
              className={`shrink-0 px-4 h-11 rounded-full text-sm font-medium transition-colors ${
                activeCategory === "ALL"
                  ? "bg-[#111] text-white shadow-[0_12px_26px_rgba(17,17,17,0.12)]"
                  : "bg-[#FAFAFA] border border-black/10 text-[#444] hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-[#DDE5F2] dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
              }`}
              data-testid="category-pill-all"
            >
              {cp.allCategories}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`shrink-0 px-4 h-11 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === c
                    ? "bg-[#F37021] text-white shadow-[0_12px_26px_rgba(243,112,33,0.18)]"
                    : "bg-[#FAFAFA] border border-black/10 text-[#444] hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-[#DDE5F2] dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                }`}
                data-testid={`category-pill-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CHINA SUPPLIERS DATABASE (only on China page) */}
      {country.code === "CN" && (
        <section className="px-5 md:px-8 pt-12 md:pt-16" data-testid="china-suppliers-card-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-[28px] border border-black/6 bg-[linear-gradient(135deg,#FFF7F0_0%,#FFFFFF_48%,#FFF0E5_100%)] p-7 text-[#111] shadow-[0_24px_60px_rgba(16,24,40,0.12)] sm:p-10 md:rounded-[32px] md:p-12 dark:border-white/10 dark:bg-[linear-gradient(135deg,#101010_0%,#080808_52%,#151515_100%)] dark:text-white dark:shadow-[0_24px_60px_rgba(0,0,0,0.34)]"
            >
              <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-[#F37021]/30 blur-[120px] pointer-events-none" />
              <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-[#F37021]/15 blur-[120px] pointer-events-none" />

              <div className="relative grid lg:grid-cols-12 gap-8 md:gap-10 items-center">
                <div className="lg:col-span-8">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.3em] uppercase text-[#F37021] mb-3">
                    <Database className="w-4 h-4" />
                    — {cp.chinaSuppliersTitle}
                  </span>
                  <h2
                    className="mb-4 font-display text-3xl font-bold leading-[1.08] text-[#111] sm:text-4xl md:text-5xl dark:text-white"
                    data-testid="china-suppliers-card-title"
                  >
                    {cp.chinaSuppliersTitle}
                  </h2>
                  <p className="max-w-xl text-base leading-relaxed text-[#5B6473] md:text-lg dark:text-[#CFCFCF]">
                    {cp.chinaSuppliersDesc}
                  </p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link
                      to="/china-suppliers-database"
                      onClick={() => {
                        trackEvent("china_suppliers_open_click", {
                          routeName: "country_page",
                          country: { code: country.code, name: countryName },
                          metadata: { source: "china_suppliers_primary_cta" },
                        });
                      }}
                      className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white font-medium transition-all hover:-translate-y-0.5 shadow-[0_14px_30px_rgba(243,112,33,0.3)]"
                      data-testid="china-suppliers-cta"
                    >
                      <Database className="w-5 h-5" />
                      {cp.chinaSuppliersCta}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? "rotate-180" : ""}`} />
                    </Link>
                  </div>
                </div>

                <div className="lg:col-span-4">
                  <Link
                    to="/china-suppliers-database"
                    onClick={() => {
                      trackEvent("china_suppliers_open_click", {
                        routeName: "country_page",
                        country: { code: country.code, name: countryName },
                        metadata: { source: "china_suppliers_visual_card" },
                      });
                    }}
                    className="group relative block aspect-[4/3] overflow-hidden rounded-2xl border border-black/8 bg-[#FFF8F3] dark:border-white/10 dark:bg-[#0A0A0A]"
                    data-testid="china-suppliers-visual-card"
                  >
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(243,112,33,0.35),transparent_65%)]" />
                    <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <pattern id="grid-suppliers" width="36" height="36" patternUnits="userSpaceOnUse">
                          <path d="M 36 0 L 0 0 0 36" fill="none" stroke="white" strokeWidth="1" />
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#grid-suppliers)" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-[#111] dark:text-white">
                      <div className="w-20 h-20 rounded-2xl bg-[#F37021] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                        <Database className="w-10 h-10" strokeWidth={1.5} />
                      </div>
                      <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-[#F37021]">Directory</div>
                      <div className="text-center font-display text-xl font-bold leading-tight sm:text-2xl">
                        {cp.chinaSuppliersTitle}
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* POPULAR RIGHT NOW */}
      {(() => {
        const popular = country.stores.filter((s) => s.popular);
        if (popular.length === 0) return null;
        return (
          <section className="px-5 md:px-8 pt-14 md:pt-20" data-testid="country-popular-section">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.3em] uppercase text-[#F37021] mb-3">
                    <Flame className="w-4 h-4" />
                    — {cp.popular}
                  </span>
                  <h2 className="font-display text-3xl font-bold leading-[1.08] text-[#111] sm:text-4xl md:text-5xl dark:text-white">
                    {cp.popular}
                  </h2>
                  <p className="mt-3 max-w-lg text-base text-[#555] dark:text-[#BAC4D4]">{cp.popularSub}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {popular.slice(0, 3).map((store, i) => {
                  const cat = lang === "ar" ? store.category_ar : store.category_en;
                  const desc = lang === "ar" ? store.desc_ar : store.desc_en;
                  return (
                    <motion.div
                      key={store.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="group relative overflow-hidden rounded-[28px] border border-black/6 bg-white p-7 text-[#111] shadow-[0_22px_55px_rgba(16,24,40,0.1)] transition-all hover:-translate-y-1 hover:shadow-[0_28px_65px_rgba(16,24,40,0.16)] dark:border-white/10 dark:bg-[#111] dark:text-white dark:shadow-[0_22px_55px_rgba(16,24,40,0.18)] dark:hover:shadow-[0_28px_65px_rgba(16,24,40,0.22)]"
                      data-testid={`popular-store-${store.id}`}
                    >
                      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#F37021]/25 blur-[60px] pointer-events-none" />
                      <div className="relative flex items-start justify-between mb-6">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-black/6 bg-[#FAFAFA] dark:border-white/10 dark:bg-white/10">
                          <img
                            src={store.logo}
                            alt={store.name}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              const img = e.currentTarget;
                              const parent = img.parentElement;
                              if (!parent) return;
                              img.style.display = "none";
                              const fallback = document.createElement("div");
                              fallback.className = "font-display font-bold text-lg text-[#F37021]";
                              fallback.textContent = (store.name || "").charAt(0); // textContent escapes XSS
                              parent.appendChild(fallback);
                            }}
                          />
                        </div>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F37021]/15 text-[#F37021] text-[11px] font-semibold tracking-wide uppercase">
                          <Flame className="w-3 h-3" />
                          {cp.popularBadge}
                        </span>
                      </div>

                      <h3 className="font-display text-2xl font-semibold mb-2">{store.name}</h3>
                      <div className="mb-3 text-xs uppercase tracking-wider text-[#7A8495] dark:text-[#A3A3A3]">{cat}</div>
                      <p className="mb-6 min-h-[40px] text-sm leading-relaxed text-[#5B6473] dark:text-[#D4D4D4]">{desc}</p>

                      <div className="flex items-center gap-2">
                        <a
                          href={store.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full bg-white text-sm font-medium text-[#111] transition-colors hover:bg-[#F37021] hover:text-white dark:bg-[#1B1B1B] dark:text-white dark:hover:bg-[#F37021]"
                          data-testid={`popular-visit-${store.id}`}
                        >
                          {cp.visitStore}
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        <a
                          href={buildWhatsAppUrl(t.common.whatsappStoreMsg(store.name, countryName))}
                          onClick={() => trackCountryWhatsapp("popular_store", { store_id: store.id, store_name: store.name })}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-11 w-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] text-white flex items-center justify-center transition-colors shrink-0"
                          aria-label="Order via WhatsApp"
                          data-testid={`popular-whatsapp-${store.id}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      {/* STORES GRID */}
      <section className="px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          {filteredStores.length === 0 ? (
            <div className="text-center py-20 text-[#555]" data-testid="country-no-results">
              <div className="mx-auto max-w-2xl rounded-[28px] border border-dashed border-black/10 bg-white px-8 py-16 shadow-[0_18px_45px_rgba(16,24,40,0.05)] dark:border-white/10 dark:bg-[#121212]">
                <div className="mb-3 text-4xl font-bold text-[#F37021]">0</div>
                <p className="text-lg">{cp.noResults}</p>
              </div>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
              data-testid="country-stores-grid"
            >
              {filteredStores.map((store, i) => {
                const cat = lang === "ar" ? store.category_ar : store.category_en;
                const desc = lang === "ar" ? store.desc_ar : store.desc_en;
                return (
                  <motion.div
                    key={store.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
                    className="group relative overflow-hidden rounded-[28px] border border-black/6 bg-white shadow-[0_10px_28px_rgba(16,24,40,0.05)] transition-all hover:-translate-y-1 hover:border-[#F37021]/30 hover:shadow-[0_24px_60px_rgba(16,24,40,0.1)] dark:border-white/10 dark:bg-[#121212] dark:hover:bg-[#1B1B1B]"
                    data-testid={`store-card-${store.id}`}
                  >
                    <div className="p-6">
                      {/* Logo + Category */}
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-black/5 bg-[#FAFAFA] dark:border-white/10 dark:bg-[#121212]">
                          <img
                            src={store.logo}
                            alt={store.name}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              const img = e.currentTarget;
                              const parent = img.parentElement;
                              if (!parent) return;
                              img.style.display = "none";
                              const fallback = document.createElement("div");
                              fallback.className = "font-display font-bold text-lg text-[#F37021]";
                              fallback.textContent = (store.name || "").charAt(0); // textContent escapes XSS
                              parent.appendChild(fallback);
                            }}
                          />
                        </div>
                        <span className="px-2.5 py-1 rounded-full bg-[#FDF1E8] text-[#C55810] text-[11px] font-semibold tracking-wide uppercase">
                          {cat}
                        </span>
                      </div>

                      <h3 className="mb-2 font-display text-xl font-semibold text-[#111] dark:text-white">
                        {store.name}
                      </h3>
                      <p className="min-h-[40px] text-sm leading-relaxed text-[#555] dark:text-[#BAC4D4]">
                        {desc}
                      </p>
                    </div>

                    <div className="p-4 pt-0 flex items-center gap-2 border-t border-black/5 mt-2">
                      <a
                        href={store.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 h-11 rounded-full bg-[#111] hover:bg-[#F37021] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-all hover:-translate-y-0.5"
                        data-testid={`store-visit-${store.id}`}
                      >
                        {cp.visitStore}
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <a
                        href={buildWhatsAppUrl(t.common.whatsappStoreMsg(store.name, countryName))}
                        onClick={() => trackCountryWhatsapp("store_card", { store_id: store.id, store_name: store.name })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-11 w-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] text-white flex items-center justify-center transition-all hover:-translate-y-0.5 shrink-0"
                        aria-label="Order via WhatsApp"
                        data-testid={`store-whatsapp-${store.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </a>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Can't find card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative mt-16 overflow-hidden rounded-[28px] border border-black/6 bg-[linear-gradient(135deg,#FFF7F0_0%,#FFFFFF_48%,#FFF0E5_100%)] p-7 text-[#111] shadow-[0_24px_60px_rgba(16,24,40,0.12)] sm:p-10 md:rounded-[32px] md:p-12 dark:border-white/10 dark:bg-[linear-gradient(135deg,#101010_0%,#080808_52%,#151515_100%)] dark:text-white dark:shadow-[0_24px_60px_rgba(0,0,0,0.34)]"
          >
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F37021]/25 blur-[100px] pointer-events-none" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6">
              <div>
                <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold leading-[1.1] mb-2">
                  {cp.cantFind}
                </h3>
                <p className="max-w-xl text-sm text-[#5B6473] sm:text-base dark:text-[#A3A3A3]">{cp.cantFindDesc}</p>
              </div>
              <a
                href={buildWhatsAppUrl(t.common.whatsappCountryMsg(countryName))}
                onClick={() => trackCountryWhatsapp("country_cant_find")}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full md:w-auto shrink-0 inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white font-medium transition-all hover:-translate-y-0.5 shadow-[0_12px_30px_rgba(243,112,33,0.3)]"
                data-testid="country-cant-find-cta"
              >
                {cp.sendLink}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="px-5 md:px-8 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto">
          <LeadForm country={country} />
        </div>
      </section>

      {/* SHIPPING CALCULATOR */}
      <section className="px-5 md:px-8 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto">
          <ShippingCalculator country={country} />
        </div>
      </section>

      {/* OTHER COUNTRIES */}
      <section className="px-5 md:px-8 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="font-display text-2xl font-semibold text-[#111] md:text-3xl dark:text-white">
              {cp.otherCountries}
            </h3>
            <Link
              to="/#countries"
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-[#F37021] transition-all hover:gap-2.5"
            >
              {cp.breadcrumbCountries}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {COUNTRY_CODES.filter((c) => c !== country.code).map((c) => {
              const other = COUNTRIES[c];
              const name = lang === "ar" ? other.name_ar : other.name_en;
              return (
                <Link
                  key={c}
                  to={`/country/${c.toLowerCase()}`}
                  onClick={() => {
                    trackEvent("shipping_country_select", {
                      routeName: "country_related",
                      country: { code: other.code, name },
                      metadata: { source: "other_countries" },
                    });
                  }}
                  className="group relative aspect-[5/4] rounded-[24px] overflow-hidden border border-black/6 shadow-[0_12px_28px_rgba(16,24,40,0.08)]"
                  data-testid={`other-country-${c}`}
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${other.heroImage})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-3 left-3 rtl:right-3 rtl:left-auto flex items-center gap-2">
                    <img
                      src={`https://flagcdn.com/w40/${other.flag}.png`}
                      alt={name}
                      className="w-5 h-[14px] object-cover rounded-[2px]"
                    />
                    <span className="text-white font-display font-semibold text-base">
                      {name}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Back to home link */}
          <div className="mt-14 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#555] transition-colors hover:text-[#F37021] dark:text-[#BAC4D4] dark:hover:text-[#F8A056]"
              data-testid="country-back-home"
            >
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              {cp.back}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
